import { type RefObject, useCallback, useLayoutEffect, useRef } from "react";

/** The point at the viewport's centre, as fractions of its scrollable content. */
interface Place {
  x: number;
  y: number;
}

const fraction = (offset: number, client: number, scroll: number) =>
  scroll > 0 ? (offset + client / 2) / scroll : 0;

function readPlace(viewport: HTMLElement): Place {
  return {
    x: fraction(viewport.scrollLeft, viewport.clientWidth, viewport.scrollWidth),
    y: fraction(viewport.scrollTop, viewport.clientHeight, viewport.scrollHeight),
  };
}

function returnToPlace(viewport: HTMLElement, place: Place) {
  viewport.scrollLeft = place.x * viewport.scrollWidth - viewport.clientWidth / 2;
  viewport.scrollTop = place.y * viewport.scrollHeight - viewport.clientHeight / 2;
}

/**
 * Keeps what is at the viewport's centre there across a zoom the reader makes.
 * Call `holdPlace` just before such a zoom and `releasePlace` before any other.
 */
export function useKeepPlaceOnZoom(viewportRef: RefObject<HTMLElement | null>, zoom: number) {
  const placeRef = useRef<Place | null>(null);

  // A layout effect, so the scroll moves before the rescaled pages paint.
  useLayoutEffect(() => {
    const place = placeRef.current;
    placeRef.current = null;
    const viewport = viewportRef.current;
    if (place != null && viewport != null) returnToPlace(viewport, place);
  }, [zoom, viewportRef]);

  const holdPlace = useCallback(() => {
    const viewport = viewportRef.current;
    placeRef.current = viewport == null ? null : readPlace(viewport);
  }, [viewportRef]);

  const releasePlace = useCallback(() => {
    placeRef.current = null;
  }, []);

  return { holdPlace, releasePlace };
}
